<template>
  <div>
    <!-- Reflow Dependencies - Force this component's update hook to fire when these values change -->
    <!-- This is needed for smooth reflow. Otherwise, only BaseModal's update() hook will fire -->
    <span class="hidden reflow-dependencies">
      {{ view }}{{ visibleBrands?.length }}
    </span>
    <BaseModal card-width="600px" border-radius="26px" no-padding no-toolbar no-exit @close="handleClose">
      <template #default>
        <div class="flex flex-col w-full">
          <!-- Header -->
          <div class="relative flex flex-col gap-1 pt-5 pb-2 pl-5 pr-12">
            <BaseText type="label" size="sm" class="text-text-muted">
              Start Spying on Competitors
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Track & save every ad your competitors launch.
            </BaseText>
            <button class="absolute top-5 right-5 p-1 rounded-md text-icon-normal transition-colors hover:bg-neutral-25"
            @click="handleClose">
              <TagRemoveIcon :width="16" :height="16" />
            </button>
          </div>

          <!-- Tabs -->
          <div class="flex items-start gap-4 px-5 pt-2 pb-3 border-b border-border-normal">
            <button v-for="tab in tabs" :key="tab.view"
            class="relative flex items-center p-1 rounded-lg transition-colors"
            :class="view === tab.view ? 'text-text-muted' : 'text-text-normal hover:bg-neutral-25 hover:text-text-muted'"
            @click="view = tab.view">
              <component :is="tab.icon" class="transition-colors"
              :class="view === tab.view ? 'text-icon-muted' : 'text-text-subdued'" />
              <BaseText type="label" size="sm" class="px-1">
                {{ tab.label }}
              </BaseText>
              <transition name="tab-underline">
                <div v-if="view === tab.view" class="tab-underline absolute left-0 right-0 h-px bg-icon-muted" />
              </transition>
            </button>
          </div>

          <!-- Modal Content -->
          <div ref="modalContent">
            <!-- SEARCH VIEW -->
            <div v-if="view === 'search'" class="flex flex-col gap-2 px-5 pt-5 fade-in" key="search-view">
              <div class="search-bar flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-white">
                <SearchIcon class="flex-shrink-0 text-text-subdued" />
                <input ref="searchInput" v-model="searchQuery" placeholder="Search for a brand by name..."
                class="flex-grow min-w-0 px-1 bg-transparent text-sm text-text-muted outline-none">
                <!-- Clear search button -->
                <transition>
                  <button v-if="searchQuery.length" class="flex-shrink-0 p-0.5 rounded text-text-subdued transition-colors duration-100
                  hover:bg-neutral-50 hover:text-icon-muted" @click="clearSearch">
                    <TagRemoveIcon :width="16" :height="16" />
                  </button>
                </transition>
              </div>
              <div ref="brandsList" class="brands-list flex flex-col gap-1 pb-3 pr-1 overflow-y-auto pt-1">
                <!-- Loading (no results to show yet) -->
                <div v-if="brandsLoading && !visibleBrands.length" class="flex items-center justify-center h-32">
                  <BaseLoadingLogo />
                </div>
                <!-- No results -->
                <div v-else-if="!visibleBrands.length && !nextCursor" class="flex flex-col items-center gap-3 py-10">
                  <div class="p-2 rounded-md bg-neutral-25">
                    <img src="../../assets/icons/search-page-find.svg" class="w-6 h-6">
                  </div>
                  <div class="flex flex-col items-center gap-1">
                    <BaseText type="label" size="sm" class="text-text-loud">
                      Can't find the brand you're looking for?
                    </BaseText>
                    <BaseText type="body" size="sm" class="text-text-subdued">
                      No worries, add the brand manually
                    </BaseText>
                  </div>
                  <BasePrimaryButton text="Add a Brand Manually" :icon="plusIcon" @click="view = 'manual'" />
                </div>
                <!-- Brand results (stale results are dimmed while a new search loads) -->
                <template v-else>
                  <button v-for="brand in visibleBrands" :key="brand.id"
                  class="flex items-center gap-3 w-full p-1 rounded-lg text-left transition"
                  :class="[
                    isSelected(brand) ? 'bg-primary-red-10' : isSubscribed(brand) ? 'cursor-default' : 'hover:bg-neutral-25',
                    { 'opacity-50 pointer-events-none': brandsLoading }
                  ]"
                  :disabled="isSubscribed(brand)"
                  @click="toggleBrand(brand)">
                    <BrandAvatar :avatar-src="brand.avatar_url" :brand-name="brand.name" :width="28" :height="28"
                    class="rounded" :class="{ 'filter grayscale opacity-40': isSubscribed(brand) }" />
                    <div class="flex items-center gap-2 flex-grow min-w-0" :class="{ 'opacity-40': isSubscribed(brand) }">
                      <BaseText type="label" size="sm" class="truncate"
                      :class="isSelected(brand) ? 'text-primary-red-300' : 'text-text-loud'">
                        {{ brand.name }}
                      </BaseText>
                      <BaseText v-if="brand.analytics" type="body" size="sm" class="flex-shrink-0"
                      :class="isSelected(brand) ? 'text-primary-red-300' : 'text-text-normal'">
                        {{ getAdCount(brand).toLocaleString() }} ads
                      </BaseText>
                    </div>
                    <BaseText v-if="isSubscribed(brand)" type="body" size="sm" class="flex-shrink-0 pr-2 text-text-subdued">
                      Already subscribed
                    </BaseText>
                    <transition>
                      <NewCheckmarkIcon v-if="isSelected(brand)" class="flex-shrink-0 mr-1 text-primary-red-100" />
                    </transition>
                  </button>
                  <InfiniteLoading v-if="nextCursor && !brandsLoading" @infinite="loadMoreBrands">
                    <div slot="spinner" class="flex justify-center py-2">
                      <BaseLoadingSpinnerCircle class="text-text-subdued" />
                    </div>
                    <div slot="no-more" />
                    <div slot="no-results" />
                  </InfiniteLoading>
                </template>
              </div>
            </div>

            <!-- MANUAL VIEW -->
            <div v-else class="flex flex-col gap-3 p-5 fade-in" key="manual-view" @focusout="linkTouched = true">
              <BaseInputV3
                label="Paste a link"
                placeholder="nike.com, facebook.com/ads/library/..."
                :input-value="link"
                :is-valid="!showLinkError"
                :error-message="linkError"
                @update:inputValue="link = $event"
              />
              <div class="flex items-start gap-2 p-2 rounded-lg bg-neutral-25">
                <InfoBorderIcon class="flex-shrink-0 text-text-subdued" />
                <BaseText type="body" size="sm" class="text-text-muted">
                  A brand's website, or its Facebook Ad Library page.
                </BaseText>
              </div>
            </div>
          </div>

          <!-- Folder Select, Credits, & Submit -->
          <div class="flex flex-col gap-4 w-full p-5 border-t border-border-normal">
            <div class="relative w-full z-10">
              <BaseSingleDropdown
                use-parent-width
                enable-search
                search-placeholder="Search Folders"
                label="Location"
                show-label-space
                :options="folderOptions"
                :selected-obj="selectedFolder"
                @change="selectedFolder = $event"
              >
                <template #icon>
                  <FolderIcon class="text-icon-normal" />
                </template>
              </BaseSingleDropdown>
            </div>
            <div class="flex items-center justify-between w-full">
              <!-- Spyder Credits -->
              <BaseTooltipV2 position="left" :offset="16">
                <div ref="creditsContainer" class="flex items-center gap-2 pl-0.5 py-0.5 pr-3 rounded-full
                text-text-muted transition-colors duration-100 hover:bg-neutral-25 cursor-default">
                  <img src="../../assets/icons/spyder-token.png" class="w-7 h-7">
                  <BaseText type="label" size="sm">
                    {{ creditsSpent }}/{{ getSpyderLimit }} credits
                  </BaseText>
                </div>
                <template #tooltip>
                  <div class="relative rounded-xl w-max flex flex-col gap-1 items-center p-1.5 bg-neutral-900 shadow-md cursor-default">
                    <img src="../../assets/icons/spyder-token.png" class="w-7 h-7 mb-0.5 mt-2.5">
                    <div class="w-full flex flex-col items-center gap-1 px-3 pb-3 pt-1">
                      <BaseText type="label" size="md" class="text-white">
                        Spyder Credits
                      </BaseText>
                      <BaseText v-if="creditsRemaining > 0" type="body" size="sm" class="text-text-disabled">
                        You have {{ creditsRemaining }} credits available.
                      </BaseText>
                      <BaseText v-else type="body" size="sm" class="text-text-disabled">
                        You have no remaining credits.
                      </BaseText>
                    </div>
                    <div class="arrow-right absolute left-full top-1/2 transform -translate-y-1/2" />
                  </div>
                </template>
              </BaseTooltipV2>
              <div class="flex items-center gap-4">
                <!-- Selections Indicator -->
                <button v-if="selectedBrands?.length && view === 'search'" class="relative h-5 quick-fade-in" 
                @mouseenter="selectionsIndicatorHovered = true" @mouseleave="selectionsIndicatorHovered = false"
                @click="selectedBrands = [], selectionsIndicatorHovered = false">
                  <transition name="quick-fade">
                    <BaseText v-if="selectionsIndicatorHovered" type="body" size="sm" class="text-text-disabled underline" key="deselect-action">
                      Clear Selections
                    </BaseText>
                    <BaseText v-else type="body" size="sm" class="absolute top-0 right-0 text-text-subdued w-max" key="selected-count">
                      {{ selectedBrands.length }} selected
                    </BaseText>
                  </transition>
                </button>
                <!-- Submit Button -->
                <BasePrimaryButton
                  text="Continue"
                  :disabled="continueDisabled"
                  :loading="submitLoading"
                  allow-disabled-click
                  @click="handleContinue"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'
import smoothReflow from 'vue-smooth-reflow'
import { normalizeBrandsForSubscriptionsList } from '@/utils/spyder/brandUtils'
import { parseSubscriptionLink } from '@/utils/spyder/subscriptionLinkUtils'

import BrandAvatar from '../brands/BrandAvatar.vue'
import InfiniteLoading from 'vue-infinite-loading'

// Icons
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import SearchBrandIcon from '../globals/Icons/SpyderV2Icons/SearchBrandIcon.vue'
import PlusCircleIcon from '../globals/Icons/PlusCircleIcon.vue'
import PlusIcon from '../globals/Icons/PlusIcon.vue'
import InfoBorderIcon from '../globals/Icons/InfoBorderIcon.vue'
import FolderIcon from '../globals/Icons/SpyderV2Icons/FolderIcon.vue'
import NewCheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'

const BRANDS_PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

export default {
  name: 'AddSpyderModal',
  mixins: [smoothReflow],
  components: {
    InfiniteLoading,
    BrandAvatar,
    TagRemoveIcon,
    SearchIcon,
    SearchBrandIcon,
    PlusCircleIcon,
    InfoBorderIcon,
    FolderIcon,
    NewCheckmarkIcon
  },
  props: {
    // 'search' or 'manual'
    initialView: {
      type: String,
      default: 'search'
    },
    // Prefills the manual link with this ad library page
    pageIdToAdd: {
      type: String,
      default: ''
    },
    initialFolder: {
      type: Object,
      default: () => ({ id: null, name: 'Default Folder' })
    }
  },
  data () {
    return {
      view: this.initialView,
      tabs: [
        { view: 'search', label: 'Search Brand', icon: 'SearchBrandIcon' },
        { view: 'manual', label: 'Add Manually', icon: 'PlusCircleIcon' }
      ],
      plusIcon: PlusIcon,
      selectedFolder: this.initialFolder,

      // Search view
      searchQuery: '',
      resultsQuery: '', // The query the current results were fetched for
      brandResults: [],
      nextCursor: null,
      brandsLoading: true,
      latestSearchId: 0,
      searchTimeout: null,
      selectedBrands: [],
      submitLoading: false,
      selectionsIndicatorHovered: false,

      // Manual view
      link: this.pageIdToAdd
        ? `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&view_all_page_id=${this.pageIdToAdd}&search_type=page&media_type=all`
        : '',
      linkTouched: false // Set once the link input loses focus or a submit is attempted
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getSpyderLimit']),
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getPendingSpyderBrands']),
    spyderSubscriptions () {
      return normalizeBrandsForSubscriptionsList(this.getSpyderSubscriptions, this.getPendingSpyderBrands)
    },
    subscribedBrandIds () {
      return new Set(this.spyderSubscriptions.map(brand => brand.id).filter(Boolean))
    },
    selectedBrandIds () {
      return new Set(this.selectedBrands.map(brand => brand.id))
    },
    // Brands the user is already subscribed to are only listed when they match a search
    visibleBrands () {
      if (this.resultsQuery) return this.brandResults
      return this.brandResults.filter(brand => !this.isSubscribed(brand))
    },
    folderOptions () {
      const folders = this.getFolders
        .filter(folder => folder.id !== this.selectedFolder.id)
        .sort((a, b) => a.name.localeCompare(b.name))
      if (this.selectedFolder.id) {
        folders.unshift({ id: null, name: 'Default Folder' })
      }
      return folders
    },
    // { advertiserId } | { domain } | { error } | null (empty)
    parsedLink () {
      return parseSubscriptionLink(this.link)
    },
    // The brand the link points to, if the user already tracks it (or is already adding it)
    // Used to make sure the user can't add the same brand twice
    trackedLinkBrand () {
      const { advertiserId, domain } = this.parsedLink || {}
      if (!advertiserId && !domain) return null
      return this.spyderSubscriptions.find(brand => advertiserId
        ? brand.advertiserIds.includes(advertiserId)
        : brand.domains.includes(domain)
      ) || null
    },
    linkError () {
      if (this.parsedLink?.error) return this.parsedLink.error
      if (this.trackedLinkBrand) return this.trackedLinkBrand.pending ? 'Already being added' : 'Already tracking this brand'
      return ''
    },
    // Format errors wait for the input to lose focus so they don't flash while typing
    showLinkError () {
      return !!this.linkError && (this.linkTouched || !!this.trackedLinkBrand)
    },
    // What submitting the manual tab would subscribe to
    linkTarget () {
      if (!this.parsedLink || this.linkError) return null
      return this.parsedLink
    },
    // Credits that submitting the active tab would use
    pendingCredits () {
      if (this.view === 'search') return this.selectedBrands.length
      return this.linkTarget ? 1 : 0
    },
    creditsSpent () {
      return this.spyderSubscriptions.length + this.pendingCredits
    },
    creditsRemaining () {
      return Math.max(this.getSpyderLimit - this.creditsSpent, 0)
    },
    isOverCreditLimit () {
      return this.creditsSpent > this.getSpyderLimit
    },
    continueDisabled () {
      if (this.isOverCreditLimit) return true
      if (this.view === 'search') return this.selectedBrands.length === 0
      return !this.linkTarget
    }
  },
  watch: {
    searchQuery () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(this.searchBrands, SEARCH_DEBOUNCE_MS)
    }
  },
  mounted () {
    // With no query, the first page is the most popular brands
    this.searchBrands()
    if (this.$refs.searchInput) this.$refs.searchInput.focus()
    this.$nextTick(() => {
      if (!this.$refs.modalContent) return
      this.$smoothReflow({
        el: this.$refs.modalContent,
        property: 'height',
        transition: 'height 300ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    clearTimeout(this.searchTimeout)
    this.$unsmoothReflow({ el: this.$refs.modalContent })
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    ...mapActions('BoardsModule', ['fetchFolders']),
    async searchBrands () {
      // Drop responses from searches that have since been replaced
      const searchId = ++this.latestSearchId
      const query = this.searchQuery.trim()
      this.brandsLoading = true
      try {
        const { data: response } = await ForeplayAPI.Search.searchSpyderBrands(query, { limit: BRANDS_PAGE_SIZE })
        if (searchId !== this.latestSearchId) return
        this.brandResults = response.data || []
        this.nextCursor = response.next || null
        this.resultsQuery = query
        if (this.$refs.brandsList) this.$refs.brandsList.scrollTop = 0
      } catch (error) {
        if (searchId !== this.latestSearchId) return
        console.error(error)
        this.brandResults = []
        this.nextCursor = null
        this.resultsQuery = query
        this.$showAlert({
          type: 'error',
          message: 'Failed to search brands. See console for details.'
        })
      } finally {
        if (searchId === this.latestSearchId) this.brandsLoading = false
      }
    },
    async loadMoreBrands ($state) {
      const searchId = this.latestSearchId
      try {
        const { data: response } = await ForeplayAPI.Search.searchSpyderBrands(
          this.resultsQuery,
          { cursor: this.nextCursor, limit: BRANDS_PAGE_SIZE }
        )
        if (searchId !== this.latestSearchId) return
        // The same brand can be returned on more than one page
        const loadedIds = new Set(this.brandResults.map(brand => brand.id))
        const newBrands = (response.data || []).filter(brand => !loadedIds.has(brand.id))
        this.brandResults = this.brandResults.concat(newBrands)
        this.nextCursor = response.next || null
        $state.loaded()
      } catch (error) {
        if (searchId !== this.latestSearchId) return
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to load more brands. See console for details.'
        })
        $state.complete()
      }
    },
    clearSearch () {
      this.searchQuery = ''
      this.$refs.searchInput.focus()
    },
    isSelected (brand) {
      return this.selectedBrandIds.has(brand.id)
    },
    isSubscribed (brand) {
      return !!brand.is_subscribed || this.subscribedBrandIds.has(brand.id)
    },
    getAdCount (brand) {
      return (brand.analytics?.active_count || 0) + (brand.analytics?.inactive_count || 0)
    },
    toggleBrand (brand) {
      if (this.isSubscribed(brand)) return
      if (this.isSelected(brand)) {
        this.selectedBrands = this.selectedBrands.filter(selected => selected.id !== brand.id)
        return
      }
      if (this.creditsSpent >= this.getSpyderLimit) {
        this.animateCreditShake()
        return
      }
      this.selectedBrands = [...this.selectedBrands, brand]
    },
    handleContinue () {
      if (this.submitLoading) return
      if (this.isOverCreditLimit) {
        this.animateCreditShake()
        return
      }
      if (this.continueDisabled) {
        // Show why the link can't be added
        if (this.view === 'manual') this.linkTouched = true
        return
      }
      if (this.view === 'search') this.subscribeToSelectedBrands()
      else this.addBrandByLink()
    },
    async subscribeToSelectedBrands () {
      const folderId = this.selectedFolder.id
      this.submitLoading = true
      try {
        const { data: result } = await ForeplayAPI.Spyder.bulkSubscribeToBrands(
          this.selectedBrands.map(brand => ({ brandId: brand.id, folderId }))
        )
        await this.handleSubscribeResult(result, folderId)
      } catch (error) {
        console.error('Error subscribing to brands', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to subscribe to brands. See console for details.'
        })
      } finally {
        this.submitLoading = false
      }
    },
    async addBrandByLink () {
      const { advertiserId, domain } = this.linkTarget
      this.submitLoading = true
      try {
        const folderId = this.selectedFolder.id
        const { data: result } = advertiserId
          ? await ForeplayAPI.Spyder.subscribeToAdvertisers([advertiserId], folderId)
          : await ForeplayAPI.Spyder.subscribeToDomains([domain], folderId)
        await this.handleSubscribeResult(result, folderId)
      } catch (error) {
        console.error('Error adding brand by link', error)
        this.$showAlert({
          type: 'error',
          message: error.response?.data?.message || 'Failed to add brand. See console for details.'
        })
      } finally {
        this.submitLoading = false
      }
    },
    // Brands that already exist come back under `subscribed`. Ones the backend has to build first come back
    // under `pending` and show as in-progress rows until they resolve (the brands view polls for that).
    async handleSubscribeResult (result, folderId) {
      const subscribed = result?.subscribed || []
      const pending = result?.pending || []
      // Subscribed brands are read back from the search index, so give it a moment to update.
      // Pending brands are listed straight from the db.
      if (subscribed.length) await new Promise(resolve => setTimeout(resolve, 2000))
      await this.refreshSpyderBrands()
      if (pending.length) {
        const brandLabel = pending.length === 1 ? (pending[0].name || pending[0].ref) : `${pending.length} brands`
        this.$showAlert({
          type: 'info',
          message: `We're adding ${brandLabel} to Spyder. This can take some time.`,
          timeout: 5000
        })
      }
      // Subscribed brands match on their brand id; pending ones on their advertiser id or domain,
      // which still matches once they resolve into a subscribed brand
      const addedBrandKeys = [
        ...subscribed.map(brand => brand.id),
        ...pending.map(brand => brand.ref)
      ].filter(Boolean)
      this.$emit('scrollFolder', folderId, addedBrandKeys)
      this.$emit('spyderAdded')
      this.$emit('close')
    },
    async refreshSpyderBrands () {
      // The subscribe request only returns ids, so re-fetch the full brands and the folders 
      // (the backend writes the new brands into the selected folder) before handing off to the parent.
      try {
        await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 sec to account for db update delays
        await this.fetchFolders()
        await this.fetchSpyderSubscriptions()
      } catch (error) {
        console.error('Error refreshing spyder brands', error)
        this.$showAlert({
          type: 'warning',
          message: 'Brands were added, but your brand list failed to refresh. Please reload the page.'
        })
      }
    },
    handleClose () {
      if (this.submitLoading) return
      this.$emit('close')
    },
    animateCreditShake () {
      const creditsContainer = this.$refs.creditsContainer
      if (!creditsContainer) return
      creditsContainer.classList.add('animate-shake')
      setTimeout(() => { creditsContainer.classList.remove('animate-shake') }, 300)
    }
  }
}
</script>

<style scoped>
.tab-underline {
  /* Sits on the tab bar's bottom border (tab bar has 12px bottom padding) */
  top: calc(100% + 12px);
}
.tab-underline-enter-active, .tab-underline-leave-active {
  transition: transform 150ms ease-in-out;
}
.tab-underline-enter, .tab-underline-leave-to {
  transform: scaleX(0);
}

.search-bar {
  box-shadow: 0px 1px 2px 0px rgba(9, 25, 72, 0.08), 0px 0px 0px 1px #ECEFF3;
}
.search-bar input::placeholder {
  color: #5E6678; /* text-text-normal */
  transition: color 150ms ease-in-out;
}
.search-bar:focus-within input::placeholder {
  color: #A4ABB8; /* text-text-disabled */
}

.brands-list {
  max-height: min(300px, calc(100vh - 652px));
  mask-image: linear-gradient(to bottom, transparent 0%, black 4px, black 100%);
}
.brands-list::-webkit-scrollbar {
  width: 3px;
}
.brands-list::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}
.brands-list::-webkit-scrollbar-button:single-button:vertical:decrement {
  height: 4px;
  background-color: transparent;
}

.arrow-right {
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid #06070F;
}

.quick-fade-enter-active, .quick-fade-leave-active {
  transition: opacity 50ms ease-in-out;
}
.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to, .quick-fade-enter-from, .quick-fade-enter, .quick-fade-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from, .quick-fade-enter-to, .quick-fade-leave-from {
  opacity: 1;
}

.animate-shake {
  animation: shake 0.3s cubic-bezier(.36,.07,.19,.97) both;
}
@keyframes shake {
  0% {
    transform: translateX(0);
    background-color: transparent;
  }
  25% { transform: translateX(3px) }
  50% {
    transform: translateX(-3px);
    background-color: #FDF2F1;
    color: #AB2D27;
  }
  75% { transform: translateX(2px) }
  100% {
    transform: translateX(0);
    background-color: transparent;
  }
}

.quick-fade-in {
  animation: fadeIn 100ms ease-in-out;
}
.fade-in {
  animation: fadeIn 200ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
